import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { Loader2, ExternalLink, FileText, Send, Mail, Copy, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import OnboardingTimeline from "./OnboardingTimeline";

/**
 * Renders the drill-down panel content for an active tile in the
 * OnboardingStatusDashboard. Switches on `tile` prop (customer/contract/esign/dd).
 */
export default function TilePanel({ tile, envelopes = [], onOpenEnvelope, onResumeSession, onScrollToStep }) {
  if (tile === "customer") return <CustomerPanel onResumeSession={onResumeSession} onScrollToStep={onScrollToStep} />;
  if (tile === "contract") return <ContractPanel onScrollToStep={onScrollToStep} />;
  if (tile === "esign") return <EsignPanel envelopes={envelopes} onOpenEnvelope={onOpenEnvelope} onScrollToStep={onScrollToStep} />;
  if (tile === "dd") return <DirectDebitPanel onScrollToStep={onScrollToStep} />;
  return null;
}

// ---------- Customer ----------
function CustomerPanel({ onResumeSession, onScrollToStep }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSfa, setExpandedSfa] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/aus/onboardings-recent", { params: { limit: 10 } });
        setSessions(r.data?.sessions || []);
      } catch (e) {
        toast.error("Failed to load onboarding sessions");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PanelShell
      title="Recent onboardings"
      subtitle="Last 10 customers · click a row to see the full timeline"
      actionLabel="Go to Customer step →"
      onAction={() => onScrollToStep?.(1)}
    >
      {loading && <PanelLoading />}
      {!loading && sessions.length === 0 && <PanelEmpty>No onboardings yet</PanelEmpty>}
      {!loading && sessions.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 px-2 w-6"></th>
              <th className="text-left py-1.5 px-2">Customer</th>
              <th className="text-left py-1.5 px-2">Business</th>
              <th className="text-left py-1.5 px-2">SFA</th>
              <th className="text-left py-1.5 px-2">Stage</th>
              <th className="text-left py-1.5 px-2">Started</th>
              <th className="text-left py-1.5 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s, i) => {
              const isExpanded = expandedSfa === s.sfa_number;
              return (
                <Fragment key={`${s.sfa_number}-${i}`}>
                  <tr
                    className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedSfa(isExpanded ? null : s.sfa_number)}
                    data-testid={`customer-row-${i}`}
                  >
                    <td className="py-1.5 px-2">
                      <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </td>
                    <td className="py-1.5 px-2 font-medium">{s.customer_name || "—"}</td>
                    <td className="py-1.5 px-2 max-w-[180px] truncate">{s.business_name || "—"}</td>
                    <td className="py-1.5 px-2 font-mono text-[11px]">{s.sfa_number}</td>
                    <td className="py-1.5 px-2"><Tag>{s.stage}</Tag></td>
                    <td className="py-1.5 px-2 text-slate-600">{fmtTime(s.started_at)}</td>
                    <td className="py-1.5 px-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); onResumeSession?.(s); }}
                        className="text-indigo-700 hover:underline text-[11px]"
                      >
                        Resume
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-4 py-4">
                        <h5 className="text-xs font-semibold text-slate-700 mb-3">
                          Timeline · {s.sfa_number}
                        </h5>
                        <OnboardingTimeline sfaNumber={s.sfa_number} showCurrentState />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </PanelShell>
  );
}

// ---------- Contract ----------
function ContractPanel({ onScrollToStep }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/aus/contracts-recent", { params: { limit: 10 } });
        setContracts(r.data?.contracts || []);
      } catch (e) {
        toast.error("Failed to load contracts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PanelShell
      title="Recent contracts"
      subtitle="Generated loan agreements"
      actionLabel="Go to Contract step →"
      onAction={() => onScrollToStep?.(5)}
    >
      {loading && <PanelLoading />}
      {!loading && contracts.length === 0 && <PanelEmpty>No contracts yet</PanelEmpty>}
      {!loading && contracts.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 px-2">SFA</th>
              <th className="text-left py-1.5 px-2">Business</th>
              <th className="text-left py-1.5 px-2">Template</th>
              <th className="text-left py-1.5 px-2">Generated</th>
              <th className="text-left py-1.5 px-2">Status</th>
              <th className="text-left py-1.5 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-slate-50" data-testid={`contract-row-${i}`}>
                <td className="py-1.5 px-2 font-mono text-[11px]">{c.sfa_number}</td>
                <td className="py-1.5 px-2 max-w-[160px] truncate">{c.business_name}</td>
                <td className="py-1.5 px-2 text-slate-600">{c.template}</td>
                <td className="py-1.5 px-2 text-slate-600">{fmtTime(c.created_at)}</td>
                <td className="py-1.5 px-2"><Tag>{c.status}</Tag></td>
                <td className="py-1.5 px-2">
                  {c.file_url ? (
                    <a href={c.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-[11px] inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Open
                    </a>
                  ) : (
                    <span className="text-slate-400 text-[11px]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PanelShell>
  );
}

// ---------- E-signature ----------
function EsignPanel({ envelopes, onOpenEnvelope, onScrollToStep }) {
  return (
    <PanelShell
      title="DocuSign envelopes"
      subtitle={`${envelopes.length} envelope${envelopes.length === 1 ? "" : "s"}`}
      actionLabel="Go to E-signature step →"
      onAction={() => onScrollToStep?.(5)}
    >
      {envelopes.length === 0 && <PanelEmpty>No envelopes yet</PanelEmpty>}
      {envelopes.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 px-2">Subject</th>
              <th className="text-left py-1.5 px-2">Status</th>
              <th className="text-left py-1.5 px-2">Created</th>
              <th className="text-left py-1.5 px-2">Signers</th>
              <th className="text-left py-1.5 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {envelopes.slice(0, 10).map((e, i) => (
              <tr key={e.envelope_id || i} className="border-b last:border-0 hover:bg-slate-50" data-testid={`esign-row-${i}`}>
                <td className="py-1.5 px-2 max-w-[260px] truncate" title={e.subject || e.email_subject}>{e.subject || e.email_subject || e.envelope_id?.slice(0, 12)}</td>
                <td className="py-1.5 px-2"><Tag tone={statusTone(e.status)}>{e.status || "—"}</Tag></td>
                <td className="py-1.5 px-2 text-slate-600">{fmtTime(e.created_date_time || e.created_at)}</td>
                <td className="py-1.5 px-2 text-slate-600">{e.signer_count ?? (e.recipients?.length || "—")}</td>
                <td className="py-1.5 px-2">
                  <button onClick={() => onOpenEnvelope?.(e)} className="text-violet-700 hover:underline text-[11px] inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PanelShell>
  );
}

// ---------- Direct Debit ----------
function DirectDebitPanel({ onScrollToStep }) {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSfa, setExpandedSfa] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/aus/mandates-checklist", { params: { limit: 10 } });
      setMandates(r.data?.mandates || []);
    } catch (e) {
      toast.error("Failed to load mandates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // refresh every 10s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Authorisation link copied");
  };

  return (
    <PanelShell
      title="Direct Debit mandates"
      subtitle={`${mandates.length} mandate${mandates.length === 1 ? "" : "s"} · click a row for full onboarding timeline · auto-refresh every 10s`}
      actionLabel="Go to Direct Debit step →"
      onAction={() => onScrollToStep?.(6)}
    >
      {loading && <PanelLoading />}
      {!loading && mandates.length === 0 && <PanelEmpty>No mandates issued yet</PanelEmpty>}
      {!loading && mandates.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <tr>
              <th className="text-left py-1.5 px-2 w-6"></th>
              <th className="text-left py-1.5 px-2">SFA / Customer</th>
              <th className="text-left py-1.5 px-2">Scheme</th>
              <th className="text-left py-1.5 px-2">DD Issued</th>
              <th className="text-left py-1.5 px-2">SMS Sent</th>
              <th className="text-left py-1.5 px-2">Read</th>
              <th className="text-left py-1.5 px-2">Opened</th>
              <th className="text-left py-1.5 px-2">Complete</th>
              <th className="text-left py-1.5 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mandates.map((m, i) => {
              const isExpanded = expandedSfa === m.sfa_number;
              return (
                <Fragment key={m.billing_request_id || i}>
                  <tr
                    className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedSfa(isExpanded ? null : m.sfa_number)}
                    data-testid={`mandate-row-${i}`}
                  >
                    <td className="py-1.5 px-2">
                      <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <div className="font-mono text-[11px]">{m.sfa_number}</div>
                      <div className="text-[11px] text-slate-600 max-w-[180px] truncate" title={m.business_name}>
                        {m.customer_name || "—"} · {m.business_name || ""}
                      </div>
                    </td>
                    <td className="py-1.5 px-2"><Tag tone="blue">{m.scheme_label}</Tag></td>
                    <Cell ok={m.dd_issued} />
                    <Cell ok={m.sms_sent} subline={m.sms_to ? `${m.sms_to}` : null} />
                    <Cell ok={m.read} />
                    <Cell ok={m.opened} subline={m.opened ? "link tapped" : null} />
                    <Cell ok={m.complete} highlight />
                    <td className="py-1.5 px-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {m.authorisation_url && (
                        <>
                          <button
                            onClick={() => copyLink(m.authorisation_url)}
                            className="text-slate-700 hover:underline text-[11px] inline-flex items-center gap-1 mr-2"
                            data-testid={`mandate-copy-${i}`}
                          >
                            <Copy className="w-3 h-3" /> Copy link
                          </button>
                          <a
                            href={m.authorisation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-700 hover:underline text-[11px] inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Open
                          </a>
                        </>
                      )}
                    </td>
                  </tr>
                  {isExpanded && m.sfa_number && (
                    <tr className="bg-slate-50">
                      <td colSpan={9} className="px-4 py-4">
                        <h5 className="text-xs font-semibold text-slate-700 mb-3">
                          Full onboarding timeline · {m.sfa_number}
                        </h5>
                        <OnboardingTimeline sfaNumber={m.sfa_number} showCurrentState />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </PanelShell>
  );
}

// ---------- Shared components ----------
function PanelShell({ title, subtitle, actionLabel, onAction, children }) {
  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="tile-panel">
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
          {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
        </div>
        {actionLabel && (
          <Button size="sm" variant="outline" onClick={onAction} className="h-7 text-xs" data-testid="tile-panel-action">
            {actionLabel}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">{children}</div>
    </div>
  );
}

function PanelLoading() {
  return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
}
function PanelEmpty({ children }) {
  return <div className="text-center text-xs text-muted-foreground py-8">{children}</div>;
}

function Cell({ ok, subline, highlight = false }) {
  return (
    <td className="py-1.5 px-2">
      <div className="flex items-center gap-1">
        <span className={
          "inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold " +
          (ok
            ? (highlight ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-800")
            : "bg-slate-100 text-slate-400")
        }>
          {ok ? "✓" : "○"}
        </span>
        {subline && <span className="text-[10px] text-slate-500">{subline}</span>}
      </div>
    </td>
  );
}

function Tag({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-300",
    blue: "bg-blue-100 text-blue-800 border-blue-300",
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    red: "bg-red-100 text-red-800 border-red-300",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-semibold border ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (["completed", "signed"].includes(s)) return "green";
  if (["declined", "voided", "failed"].includes(s)) return "red";
  if (["sent", "delivered"].includes(s)) return "amber";
  return "slate";
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return d.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}
